import {
  keepPreviousData,
  useQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import {
  getMySelf,
  getUsers,
  getAlanQuota,
  getLoginInfoByUserId,
  clearUserMemory,
  deleteUserMemory,
  getUserMemory,
  updateUserMemory,
} from "../Api/user";
import { getUser } from "../Api/auth";
import {
  getUserById,
  editUserById,
  deleteUserById,
  createUser,
  toggleUserDisabled,
} from "../Api/user";
import { updateUser, changePassword } from "../Api/user";

export const useUsers = (
  page: number,
  page_size: number,
  search: string,
  sort_order?: string,
  sort_direction?: "asc" | "desc",
) => {
  return useQuery({
    queryKey: ["users", page, page_size, search, sort_order, sort_direction],
    queryFn: () =>
      getUsers(page, page_size, search, undefined, sort_order, sort_direction),
    staleTime: 2 * 60 * 1000,
    gcTime: 0,
    placeholderData: undefined,
  });
};

export const useUsersTable = (
  page: number,
  page_size: number,
  search: string,
  workspaceId?: number,
) => {
  return useQuery({
    queryKey: ["users", page, page_size, search, workspaceId],
    queryFn: () => getUsers(page, page_size, search, workspaceId),
    placeholderData: keepPreviousData,
  });
};

export const useUser = () => {
  return useQuery({
    queryKey: ["user"],
    queryFn: getUser,
    placeholderData: keepPreviousData,
    staleTime: 300000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchInterval: 60000,
    retry: false,
  });
};

export const useMyself = () => {
  return useQuery({
    queryKey: ["myself"],
    queryFn: () => getMySelf(),
    staleTime: 300000,
    refetchOnWindowFocus: false,
  });
};

export const useUserById = (userId?: string | null) => {
  return useQuery({
    queryKey: ["user", userId],
    queryFn: () => getUserById(userId ?? ""),
  });
};

export const useEditUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      userId,
      name,
      surname,
      email,
      evaluationLimit,
      maxCompanyLimit,
      password,
      modelIds,
      certCategoryIds,
      workspaceId,
      features,
      selectedExpertIds,
      storage_limit,
      library_limit,
      show_locked_features,
      agent_ids,
    }: {
      userId: string;
      name: string;
      surname: string;
      email: string;
      evaluationLimit: number;
      maxCompanyLimit: number;
      password: string;
      modelIds: number[];
      certCategoryIds: number[];
      workspaceId: number | null;
      features: number[];
      selectedExpertIds: string[];
      storage_limit: number;
      library_limit: number;
      show_locked_features: boolean;
      agent_ids?: number[];
    }) =>
      editUserById(
        userId,
        name,
        surname,
        email,
        evaluationLimit,
        maxCompanyLimit,
        password,
        modelIds,
        certCategoryIds,
        workspaceId,
        features,
        selectedExpertIds,
        storage_limit,
        library_limit,
        show_locked_features,
        agent_ids || [],
      ),
    onSuccess: (_, { userId }) => {
      queryClient.invalidateQueries({ queryKey: ["user", userId] });
      queryClient.invalidateQueries({ queryKey: ["users"] });
    },
  });
};

export const useDeleteUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => deleteUserById(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
    },
  });
};

export const useCreateUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      name,
      surname,
      password,
      email,
      evaluationLimit,
      maxCompanies,
      modelIds,
      storageLimit,
      libraryLimit,
      certCategoryIds,
      isSso,
      workspaceId,
      features,
      experts,
      show_locked_features,
      agent_ids,
    }: {
      name: string;
      surname: string;
      password: string | null;
      email: string;
      evaluationLimit: number;
      maxCompanies: number;
      modelIds: number[];
      storageLimit: number;
      libraryLimit: number;
      certCategoryIds: number[];
      isSso: boolean;
      workspaceId: number | null;
      features: number[];
      experts: string[];
      show_locked_features: boolean;
      agent_ids?: number[];
    }) =>
      createUser(
        name,
        surname,
        password,
        email,
        evaluationLimit,
        maxCompanies,
        modelIds,
        storageLimit,
        libraryLimit,
        certCategoryIds,
        isSso,
        workspaceId,
        features,
        experts,
        show_locked_features,
        agent_ids || [],
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
    },
  });
};

export const useUpdateUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: {
      name: string;
      surname: string;
      email?: string | undefined;
    }) => updateUser(params.name, params.surname, params.email),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user"] });
      queryClient.invalidateQueries({ queryKey: ["users"] });
    },
  });
};

export const useChangePassword = () => {
  return useMutation({
    mutationFn: ({
      currentPassword,
      newPassword,
    }: {
      currentPassword: string;
      newPassword: string;
    }) => changePassword(currentPassword, newPassword),
  });
};

export const useAlanQuota = (enabled: boolean = true) => {
  return useQuery({
    queryKey: ["alanQuota"],
    queryFn: () => getAlanQuota(),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
    enabled,
  });
};

export const useLoginInfoByUserId = (userId?: string | null) => {
  return useQuery({
    queryKey: ["loginInfo", userId],
    queryFn: () => getLoginInfoByUserId(userId ?? ""),
  });
};

export const useToggleUserDisabled = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => toggleUserDisabled(userId),
    onSuccess: (_, userId) => {
      queryClient.invalidateQueries({ queryKey: ["user", userId] });
      queryClient.invalidateQueries({ queryKey: ["users"] });
    },
  });
};

export const useUserMemory = () => {
  return useQuery({
    queryKey: ["userMemory"],
    queryFn: getUserMemory,
  });
};

export const useUpdateUserMemory = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      line,
      text,
      expected,
    }: {
      line: number;
      text: string;
      expected: string;
    }) => updateUserMemory(line, text, expected),

    onSuccess: (data) => {
      queryClient.setQueryData(["userMemory"], data);
    },
  });
};

export const useDeleteUserMemory = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ line, expected }: { line: number; expected: string }) =>
      deleteUserMemory(line, expected),

    onSuccess: (data) => {
      queryClient.setQueryData(["userMemory"], data);
    },
  });
};

export const useClearUserMemory = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: clearUserMemory,

    onSuccess: (data) => {
      queryClient.setQueryData(["userMemory"], data);
    },
  });
};
